import { setDateWithTimezone } from '@systemeio/util'
import { DateFormatEnum, TimeFormatEnum } from 'shared/enums/date-time-format-enum'
import { LanguageCodeEnum } from 'shared/enums/language-code-enum'
import { UserInterface } from 'shared/types/user-interface'

export const dateTimeFormat = (
  date: string | Date,
  format: {
    dateFormat?: DateFormatEnum
    timeFormat?: TimeFormatEnum
    timeZone?: UserInterface['timezone']
  },
) => {
  const isShort =
    format.timeFormat === TimeFormatEnum.hourMinuteAmPm ||
    format.timeFormat === TimeFormatEnum.hourMinuteSecondAmPm
  const nonFormatDate = (typeof date === 'string' ? new Date(date) : date).toLocaleString(
    getFormat(format.dateFormat),
    {
      second:
        format.timeFormat === TimeFormatEnum.hourMinuteAmPm ||
        format.timeFormat === TimeFormatEnum.hourMinute
          ? undefined
          : '2-digit',
      minute: '2-digit',
      hour: '2-digit',
      day: '2-digit',
      month: '2-digit',
      hourCycle: isShort ? 'h12' : 'h23',
      year: 'numeric',
      timeZone: format.timeZone,
    },
  )
  return isShort ? nonFormatDate.replace('am', 'AM').replace('pm', 'PM') : nonFormatDate
}

export const timeFormat = (
  date: string | Date,
  format: {
    dateFormat?: DateFormatEnum
    timeFormat?: TimeFormatEnum
    timeZone?: UserInterface['timezone']
  },
) => {
  const isShort =
    format.timeFormat === TimeFormatEnum.hourMinuteAmPm ||
    format.timeFormat === TimeFormatEnum.hourMinuteSecondAmPm
  const nonFormatDate = (typeof date === 'string' ? new Date(date) : date).toLocaleString(
    getFormat(format.dateFormat),
    {
      second:
        format.timeFormat === TimeFormatEnum.hourMinuteAmPm ||
        format.timeFormat === TimeFormatEnum.hourMinute
          ? undefined
          : '2-digit',
      minute: '2-digit',
      hour: '2-digit',
      hourCycle: isShort ? 'h12' : 'h23',
      timeZone: format.timeZone,
    },
  )
  return isShort ? nonFormatDate.replace('am', 'AM').replace('pm', 'PM') : nonFormatDate
}

export const dateFormat = (
  date: string | Date,
  format: { dateFormat?: DateFormatEnum; timeZone?: UserInterface['timezone'] },
) =>
  (typeof date === 'string' ? new Date(date) : date).toLocaleString(getFormat(format.dateFormat), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: format.timeZone,
  })

const getFormat = (dateFormat?: DateFormatEnum): string | undefined => {
  switch (dateFormat) {
    case DateFormatEnum.dateFirst:
      return 'en-GB'
    case DateFormatEnum.monthFirst:
      return 'en-US'

    default:
      return undefined
  }
}

export const removeTimezoneFromString = (date: string) => {
  if (date.match(/([-+]\d+:?\d+)$/)) {
    return date.replace(/([-+]\d{2}:?\d{2})$/, '')
  } else {
    return date
  }
}

export const toShortDate = (date: string, intlLocale: LanguageCodeEnum) =>
  new Date(Date.parse(date)).toLocaleString(intlLocale, {
    timeZone: 'UTC',
    month: 'short',
    day: '2-digit',
  })

export const toFullDate = (date: string, intlLocale: LanguageCodeEnum) =>
  new Date(Date.parse(date)).toLocaleString(intlLocale, {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

export const toShortMonth = (date: string, intlLocale: LanguageCodeEnum) =>
  new Date(Date.parse(date)).toLocaleString(intlLocale, { timeZone: 'UTC', month: 'short' })

interface ExactDateOptions {
  intlLocale: LanguageCodeEnum
  timeZone?: UserInterface['timezone']
}

const exactDateParts = { year: 'numeric', month: 'short', day: '2-digit' } as const
const exactTimeParts = { hour: '2-digit', minute: '2-digit' } as const

const formatExact = (
  date: string | Date,
  parts: Intl.DateTimeFormatOptions,
  { intlLocale, timeZone }: ExactDateOptions,
) => {
  const value = new Date(date)
  if (Number.isNaN(value.getTime())) return ''
  return new Intl.DateTimeFormat(intlLocale, { ...parts, timeZone }).format(value)
}

/** The text of `DateTime`, for a date that goes into a string: a translation or a string prop. */
export const formatExactDateTime = (date: string | Date, options: ExactDateOptions) =>
  formatExact(date, { ...exactDateParts, ...exactTimeParts }, options)

/** `DateTime` without the time, for a value that is a day rather than a moment. */
export const formatExactDate = (date: string | Date, options: ExactDateOptions) =>
  formatExact(date, exactDateParts, options)

export const formatExactTime = (date: string | Date, options: ExactDateOptions) =>
  formatExact(date, exactTimeParts, options)

/** "April 27, 2026 • 11:03 AM": the exact moment behind a relative time. */
export const formatDateTimeLong = (date: string | Date, options: ExactDateOptions) => {
  const day = formatExact(date, { year: 'numeric', month: 'long', day: 'numeric' }, options)
  const time = formatExact(date, { hour: 'numeric', minute: '2-digit' }, options)
  return day && `${day} • ${time}`
}

export const filterPassedTime = (time: Date, timeZone?: UserInterface['timezone']) => {
  const currentDate = setDateWithTimezone('', timeZone || 'Europe/London')
  return currentDate.getTime() < time.getTime()
}
